import React, { useState } from "react";
import adminService from "../../api/services/adminService";

function sumSourceStats(stats) {
  if (!stats || typeof stats !== "object") return 0;
  return (
    Number(stats.active || 0) +
    Number(stats.expired || 0) +
    Number(stats.canceled || 0)
  );
}

/**
 * Independent date-range card: count subscriptions that started in [start, end].
 */
export default function SubscriptionPeriodCountCard({ onApplyToList, embedded = false }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const clear = () => {
    setDateFrom("");
    setDateTo("");
    setError("");
    setResult(null);
  };

  const runCount = async () => {
    setError("");
    if (!dateFrom || !dateTo) {
      setError("Please select both start and end dates.");
      return;
    }
    if (dateFrom > dateTo) {
      setError("Start date must be on or before end date.");
      return;
    }
    setLoading(true);
    try {
      const response = await adminService.getAllSubscriptions({
        date_from: dateFrom,
        date_to: dateTo,
        page: 1,
        limit: 1,
      });
      const total = Number(response?.pagination?.totalItems ?? 0);
      const purchase = response?.purchaseTypeStats || {};
      setResult({
        total,
        manual: sumSourceStats(response?.manualStats),
        revenueCat: sumSourceStats(response?.revenueCatStats),
        initial: Number(purchase.initial ?? 0),
        renew: Number(purchase.renew ?? 0),
        other: Number(purchase.other ?? 0),
        dateFrom,
        dateTo,
      });
    } catch (e) {
      setResult(null);
      setError(e?.message || "Failed to count subscriptions for this period.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={
        embedded
          ? ""
          : "mb-5 rounded-xl border border-rose-100 bg-gradient-to-br from-rose-50/80 via-white to-gray-50 p-4 sm:p-5 shadow-sm"
      }
    >
      {!embedded ? (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
          <div>
            <h3 className="text-sm sm:text-base font-bold text-gray-800">
              Subscriptions in period
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Count subscriptions started in a date range, split by initial vs renew.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-4">
          Count subscriptions started in a date range, split by initial vs renew.
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
        <label className="flex flex-col gap-1 min-w-[140px]">
          <span className="text-xs font-medium text-gray-600">Start date</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-rose-300 outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 min-w-[140px]">
          <span className="text-xs font-medium text-gray-600">End date</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-rose-300 outline-none"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={runCount}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#FF2D38] hover:bg-[#e02832] disabled:opacity-60 transition"
          >
            {loading ? "Counting…" : "Count"}
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={loading}
            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60"
          >
            Clear
          </button>
          {result && typeof onApplyToList === "function" ? (
            <button
              type="button"
              onClick={() => onApplyToList(result.dateFrom, result.dateTo)}
              disabled={loading}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-[#FF2D38] border border-rose-200 bg-white hover:bg-rose-50 disabled:opacity-60"
            >
              Show in list
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="mt-3 text-xs text-red-600 font-medium">{error}</p>
      ) : null}

      {result ? (
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="rounded-xl border border-rose-200 bg-white px-5 py-4 min-w-[160px]">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Total arrived
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">
                {result.total}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                {result.dateFrom} → {result.dateTo}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-1 min-w-0">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  Initial
                </p>
                <p className="text-2xl font-bold text-emerald-900 mt-0.5 tabular-nums">
                  {result.initial}
                </p>
                <p className="text-[10px] text-emerald-700/70 mt-0.5">
                  First purchase / manual grant
                </p>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50/70 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                  Renew
                </p>
                <p className="text-2xl font-bold text-sky-900 mt-0.5 tabular-nums">
                  {result.renew}
                </p>
                <p className="text-[10px] text-sky-700/70 mt-0.5">
                  Renewal events
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800">
              Manual: {result.manual}
            </span>
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-800">
              RevenueCat: {result.revenueCat}
            </span>
            {result.other > 0 ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">
                Other status: {result.other}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
